import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { PageLayout } from '../components/layout/PageLayout';
import { useAuth } from '../context/AuthContext';
import { api, formatMYR, formatDate, formatDateTime, parseMYRToCents, calculateProgress, openFileUrl } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Calendar as CalendarIcon,
  Upload,
  FileText,
  CreditCard,
  QrCode,
  Megaphone,
  Eye,
  Loader2,
  Building2,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  TrendingDown,
  Receipt,
  Users,
  Trash2,
  Pencil,
  AlertTriangle,
  Shield,
} from 'lucide-react';
import { cn } from '../lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const statusColors = {
  PENDING_REVIEW: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  APPROVED: 'bg-green-100 text-green-800 border-green-200',
  REJECTED: 'bg-red-100 text-red-800 border-red-200',
  ENDED: 'bg-gray-100 text-gray-800 border-gray-200',
};

const statusLabels = {
  PENDING_REVIEW: 'Pending Review',
  APPROVED: 'Active',
  REJECTED: 'Rejected',
  ENDED: 'Ended',
};

const proofStatusColors = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  VERIFIED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-800',
};

export default function ManageCampaignPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [campaign, setCampaign] = useState(null);
  const [donationProofs, setDonationProofs] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Edit form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [targetMYR, setTargetMYR] = useState('');
  const [deadline, setDeadline] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Payment details state
  const [bankName, setBankName] = useState('');
  const [bankAccountNumber, setBankAccountNumber] = useState('');
  const [accountHolderName, setAccountHolderName] = useState('');
  const [paymentInstructions, setPaymentInstructions] = useState('');
  const [qrFile, setQrFile] = useState(null);
  const [qrPreview, setQrPreview] = useState(null);

  // Update state
  const [updateMessage, setUpdateMessage] = useState('');
  const [postingUpdate, setPostingUpdate] = useState(false);

  // Receipt state
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptAmount, setReceiptAmount] = useState('');
  const [receiptCaption, setReceiptCaption] = useState('');
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [deletingReceiptId, setDeletingReceiptId] = useState(null);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [editReceiptCaption, setEditReceiptCaption] = useState('');
  const [savingReceiptEdit, setSavingReceiptEdit] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  // Verification state
  const [verifyingId, setVerifyingId] = useState(null);
  
  // Tax receipt state
  const [uploadingTaxReceiptId, setUploadingTaxReceiptId] = useState(null);
  const [taxReceiptFile, setTaxReceiptFile] = useState(null);

  const isOwner = campaign?.owner_id === user?.id;

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [campaignRes, proofsRes, receiptsRes] = await Promise.all([
        api.get(`/campaigns/${id}`),
        api.get(`/campaigns/${id}/donation-proofs`),
        api.get(`/campaigns/${id}/receipts`),
      ]);
      const c = campaignRes.data;
      setCampaign(c);
      setDonationProofs(proofsRes.data);
      setReceipts(receiptsRes.data);

      // Populate form
      setTitle(c.title);
      setDescription(c.description);
      setTargetMYR((c.target_cents / 100).toFixed(2));
      setDeadline(new Date(c.deadline_date));
      setImagePreview(c.image_url);
      setBankName(c.bank_name || '');
      setBankAccountNumber(c.bank_account_number || '');
      setAccountHolderName(c.account_holder_name || '');
      setPaymentInstructions(c.payment_instructions || '');
      setQrPreview(c.payment_qr_url);
    } catch (error) {
      toast.error('Failed to load campaign');
      navigate('/my-campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDetails = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('title', title.trim());
      formData.append('description', description.trim());
      formData.append('target_cents', parseMYRToCents(targetMYR).toString());
      formData.append('deadline_date', deadline.toISOString());
      if (imageFile) {
        formData.append('image', imageFile);
      }

      const response = await api.patch(`/campaigns/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCampaign(response.data);
      setImageFile(null);
      toast.success('Campaign details saved!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePaymentDetails = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('bank_name', bankName);
      formData.append('bank_account_number', bankAccountNumber);
      formData.append('account_holder_name', accountHolderName);
      formData.append('payment_instructions', paymentInstructions);
      if (qrFile) {
        formData.append('payment_qr', qrFile);
      }

      const response = await api.patch(`/campaigns/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCampaign(response.data);
      setQrFile(null);
      toast.success('Payment details saved!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyProof = async (proofId, status, rejectionReason = null) => {
    setVerifyingId(proofId);
    try {
      await api.patch(`/campaigns/${id}/donation-proofs/${proofId}/verify`, {
        status,
        rejection_reason: rejectionReason,
      });
      toast.success(status === 'VERIFIED' ? 'Donation verified!' : 'Donation rejected');
      
      // Refresh data
      const [campaignRes, proofsRes] = await Promise.all([
        api.get(`/campaigns/${id}`),
        api.get(`/campaigns/${id}/donation-proofs`),
      ]);
      setCampaign(campaignRes.data);
      setDonationProofs(proofsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify');
    } finally {
      setVerifyingId(null);
    }
  };

  const handleUploadTaxReceipt = async (proofId) => {
    if (!taxReceiptFile) {
      toast.error('Please select a tax receipt file');
      return;
    }

    setUploadingTaxReceiptId(proofId);
    try {
      const formData = new FormData();
      formData.append('file', taxReceiptFile);

      await api.post(`/campaigns/${id}/donation-proofs/${proofId}/tax-receipt`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Tax receipt sent to donor!');
      setTaxReceiptFile(null);

      // Refresh donation proofs
      const proofsRes = await api.get(`/campaigns/${id}/donation-proofs`);
      setDonationProofs(proofsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload tax receipt');
    } finally {
      setUploadingTaxReceiptId(null);
    }
  };

  const handlePostUpdate = async (e) => {
    e.preventDefault();
    if (!updateMessage.trim()) {
      toast.error('Please enter an update message');
      return;
    }

    setPostingUpdate(true);
    try {
      await api.post(`/campaigns/${id}/comments`, {
        message: updateMessage,
        is_owner_update: true,
      });
      setUpdateMessage('');
      toast.success('Update posted!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post update');
    } finally {
      setPostingUpdate(false);
    }
  };

  const handleUploadReceipt = async (e) => {
    e.preventDefault();
    if (!disclaimerAccepted) {
      toast.error('Please acknowledge the legal disclaimer before uploading');
      return;
    }
    if (!receiptFile) {
      toast.error('Please select a file');
      return;
    }
    if (!receiptAmount || parseFloat(receiptAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!receiptCaption || !receiptCaption.trim()) {
      toast.error('Please enter a description for this expense');
      return;
    }

    setUploadingReceipt(true);
    try {
      const formData = new FormData();
      formData.append('file', receiptFile);
      formData.append('amount_cents', parseMYRToCents(receiptAmount).toString());
      formData.append('caption', receiptCaption.trim());

      await api.post(`/campaigns/${id}/receipts`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      // Refresh data
      const [campaignRes, receiptsRes] = await Promise.all([
        api.get(`/campaigns/${id}`),
        api.get(`/campaigns/${id}/receipts`),
      ]);
      setCampaign(campaignRes.data);
      setReceipts(receiptsRes.data);
      
      setReceiptFile(null);
      setReceiptAmount('');
      setReceiptCaption('');
      setDisclaimerAccepted(false);
      toast.success('Receipt uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload receipt');
    } finally {
      setUploadingReceipt(false);
    }
  };

  const handleDeleteReceipt = async (receiptId) => {
    setDeletingReceiptId(receiptId);
    try {
      await api.delete(`/campaigns/${id}/receipts/${receiptId}`);
      
      // Refresh data
      const [campaignRes, receiptsRes] = await Promise.all([
        api.get(`/campaigns/${id}`),
        api.get(`/campaigns/${id}/receipts`),
      ]);
      setCampaign(campaignRes.data);
      setReceipts(receiptsRes.data);
      
      toast.success('Receipt deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete receipt');
    } finally {
      setDeletingReceiptId(null);
    }
  };

  const handleEditReceipt = (receipt) => {
    setEditingReceipt(receipt);
    setEditReceiptCaption(receipt.caption || '');
  };

  const handleSaveReceiptEdit = async () => {
    if (!editReceiptCaption.trim()) {
      toast.error('Please enter a description');
      return;
    }
    
    setSavingReceiptEdit(true);
    try {
      const formData = new FormData();
      formData.append('caption', editReceiptCaption.trim());
      
      await api.patch(`/campaigns/${id}/receipts/${editingReceipt.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      // Refresh receipts
      const receiptsRes = await api.get(`/campaigns/${id}/receipts`);
      setReceipts(receiptsRes.data);
      
      setEditingReceipt(null);
      setEditReceiptCaption('');
      toast.success('Receipt updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update receipt');
    } finally {
      setSavingReceiptEdit(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
        toast.error('Please upload a JPG or PNG image');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleQrChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
        toast.error('Please upload a JPG or PNG image');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }
      setQrFile(file);
      setQrPreview(URL.createObjectURL(file));
    }
  };

  const handleReceiptChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please upload a JPG, PNG, or PDF file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File must be less than 10MB');
        return;
      }
      setReceiptFile(file);
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </PageLayout>
    );
  }

  if (!campaign || (!isOwner && !isAdmin)) {
    return null;
  }

  const progress = calculateProgress(campaign.raised_cents, campaign.target_cents);
  const pendingProofs = donationProofs.filter(p => p.status === 'PENDING');
  const verifiedProofs = donationProofs.filter(p => p.status === 'VERIFIED');

  return (
    <PageLayout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <Link to={`/campaigns/${id}`}>
            <Button variant="outline" className="gap-2 rounded-full" data-testid="view-campaign-btn">
              <Eye className="w-4 h-4" />
              View Campaign
            </Button>
          </Link>
        </div>

        {/* Campaign Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">{campaign.title}</h1>
              <Badge className={`${statusColors[campaign.status]} border`}>
                {statusLabels[campaign.status]}
              </Badge>
            </div>
          </div>

          {/* Progress Summary */}
          <Card>
            <CardContent className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-1 flex items-center gap-1">
                    <TrendingUp className="w-3 h-3 text-green-600" />
                    Collected
                  </p>
                  <p className="text-xl font-bold text-primary">
                    {formatMYR(campaign.raised_cents)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1 flex items-center gap-1">
                    <TrendingDown className="w-3 h-3 text-red-600" />
                    Spent
                  </p>
                  <p className="text-xl font-bold text-red-600">
                    {formatMYR(campaign.spent_cents || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Target</p>
                  <p className="text-xl font-bold">{formatMYR(campaign.target_cents)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Donors</p>
                  <p className="text-xl font-bold">{campaign.donation_count}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Progress</p>
                  <p className="text-xl font-bold">{progress}%</p>
                </div>
              </div>
              <Progress value={progress} className="h-3 mt-4" />
            </CardContent>
          </Card>
        </div>

        {/* Pending Donations Alert */}
        {pendingProofs.length > 0 && (
          <Card className="mb-6 border-yellow-300 bg-yellow-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Clock className="w-6 h-6 text-yellow-600" />
                <div>
                  <p className="font-medium text-yellow-800">
                    {pendingProofs.length} donation{pendingProofs.length > 1 ? 's' : ''} pending verification
                  </p>
                  <p className="text-sm text-yellow-700">
                    Review and verify donations in the Donations tab
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Management Tabs */}
        <Tabs defaultValue="donations" className="space-y-6">
          <TabsList className="bg-secondary/50 p-1 rounded-full flex-wrap">
            <TabsTrigger value="donations" className="rounded-full gap-2" data-testid="tab-donations">
              <Users className="w-4 h-4" />
              Donations
              {pendingProofs.length > 0 && (
                <Badge className="bg-yellow-500 text-white ml-1">{pendingProofs.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="details" className="rounded-full" data-testid="tab-details">
              <FileText className="w-4 h-4 mr-2" />
              Details
            </TabsTrigger>
            <TabsTrigger value="payment" className="rounded-full" data-testid="tab-payment">
              <CreditCard className="w-4 h-4 mr-2" />
              Payment
            </TabsTrigger>
            <TabsTrigger value="spending" className="rounded-full" data-testid="tab-spending">
              <Receipt className="w-4 h-4 mr-2" />
              Spending
            </TabsTrigger>
            <TabsTrigger value="updates" className="rounded-full" data-testid="tab-updates">
              <Megaphone className="w-4 h-4 mr-2" />
              Updates
            </TabsTrigger>
          </TabsList>

          {/* Donations Tab */}
          <TabsContent value="donations" className="space-y-6">
            {/* Pending Verifications */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-yellow-600" />
                  Pending Verification ({pendingProofs.length})
                </CardTitle>
                <CardDescription>
                  Review donation proofs submitted by donors
                </CardDescription>
              </CardHeader>
              <CardContent>
                {pendingProofs.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    No pending donations to verify
                  </p>
                ) : (
                  <div className="space-y-4">
                    {pendingProofs.map((proof) => (
                      <div key={proof.id} className="p-4 border rounded-xl space-y-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium">{proof.donor_username || proof.donor_email}</p>
                            <p className="text-sm text-muted-foreground">{formatDateTime(proof.created_at)}</p>
                          </div>
                          <span className="text-xl font-bold text-primary">{formatMYR(proof.amount_cents)}</span>
                        </div>
                        {proof.message && (
                          <p className="text-sm text-muted-foreground bg-secondary/50 p-2 rounded">
                            "{proof.message}"
                          </p>
                        )}
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => openFileUrl(proof.file_url, `proof_${proof.id}`)}
                            className="text-sm text-primary hover:underline flex items-center gap-1"
                          >
                            <FileText className="w-4 h-4" />
                            View Proof
                          </button>
                        </div>
                        <div className="flex gap-2 pt-2">
                          <Button
                            onClick={() => handleVerifyProof(proof.id, 'VERIFIED')}
                            disabled={verifyingId === proof.id}
                            className="flex-1 rounded-full bg-green-600 hover:bg-green-700"
                          >
                            {verifyingId === proof.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Verify
                              </>
                            )}
                          </Button>
                          <Button
                            onClick={() => handleVerifyProof(proof.id, 'REJECTED', 'Invalid proof')}
                            disabled={verifyingId === proof.id}
                            variant="destructive"
                            className="flex-1 rounded-full"
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Verified Donations */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Verified Donations ({verifiedProofs.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {verifiedProofs.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    No verified donations yet
                  </p>
                ) : (
                  <div className="space-y-4">
                    {campaign.is_tax_deductible && (
                      <div className="p-3 bg-emerald-50 rounded-xl text-sm text-emerald-800 flex items-start gap-2">
                        <Receipt className="w-5 h-5 mt-0.5 flex-shrink-0" />
                        <p>This is a tax-deductible campaign. You can issue official tax receipts to verified donors below.</p>
                      </div>
                    )}
                    {verifiedProofs.map((proof) => (
                      <div key={proof.id} className="p-4 bg-green-50 rounded-xl space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{proof.donor_username || proof.donor_email}</p>
                            <p className="text-sm text-muted-foreground">Verified: {formatDateTime(proof.verified_at)}</p>
                          </div>
                          <span className="font-bold text-green-700 text-lg">{formatMYR(proof.amount_cents)}</span>
                        </div>
                        
                        {/* Tax Receipt Section - Only for tax-deductible campaigns */}
                        {campaign.is_tax_deductible && (
                          <div className="pt-3 border-t border-green-200">
                            {proof.tax_receipt_url ? (
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-sm text-green-700">
                                  <CheckCircle className="w-4 h-4" />
                                  <span>Tax receipt issued on {formatDate(proof.tax_receipt_issued_at)}</span>
                                </div>
                                <button
                                  onClick={() => openFileUrl(proof.tax_receipt_url, `tax_receipt_${proof.id}`)}
                                  className="text-sm text-primary hover:underline flex items-center gap-1"
                                >
                                  <Eye className="w-4 h-4" />
                                  View Receipt
                                </button>
                              </div>
                            ) : (
                              <div className="space-y-2">
                                <p className="text-sm text-muted-foreground">Issue tax receipt to this donor:</p>
                                <div className="flex items-center gap-2">
                                  <input
                                    type="file"
                                    accept="image/jpeg,image/png,image/jpg,application/pdf"
                                    onChange={(e) => {
                                      setTaxReceiptFile(e.target.files?.[0] || null);
                                      setUploadingTaxReceiptId(proof.id);
                                    }}
                                    className="text-sm file:mr-2 file:py-1 file:px-3 file:rounded-full file:border-0 file:text-sm file:bg-primary file:text-white hover:file:bg-primary/90"
                                    id={`tax-receipt-${proof.id}`}
                                  />
                                  {uploadingTaxReceiptId === proof.id && taxReceiptFile && (
                                    <Button
                                      size="sm"
                                      onClick={() => handleUploadTaxReceipt(proof.id)}
                                      disabled={uploadingTaxReceiptId === proof.id && !taxReceiptFile}
                                      className="rounded-full"
                                    >
                                      {uploadingTaxReceiptId === proof.id && !taxReceiptFile ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                      ) : (
                                        <>
                                          <Upload className="w-4 h-4 mr-1" />
                                          Send
                                        </>
                                      )}
                                    </Button>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Details Tab */}
          <TabsContent value="details" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Campaign Details</CardTitle>
                <CardDescription>Edit your campaign information</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label>Campaign Image</Label>
                  <div className="flex flex-col items-center gap-4">
                    {imagePreview ? (
                      <div className="relative w-full max-w-md aspect-[16/9] rounded-xl overflow-hidden border">
                        <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                        <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 hover:opacity-100 transition-opacity cursor-pointer">
                          <span className="text-white font-medium">Change Image</span>
                          <input type="file" accept="image/jpeg,image/png,image/jpg" onChange={handleImageChange} className="hidden" />
                        </label>
                      </div>
                    ) : (
                      <label className="w-full max-w-md aspect-[16/9] rounded-xl border-2 border-dashed border-border hover:border-primary/50 transition-colors cursor-pointer flex flex-col items-center justify-center gap-3 bg-secondary/30">
                        <Upload className="w-10 h-10 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">Upload image</p>
                        <input type="file" accept="image/jpeg,image/png,image/jpg" onChange={handleImageChange} className="hidden" />
                      </label>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-title">Title</Label>
                  <Input id="edit-title" value={title} onChange={(e) => setTitle(e.target.value)} className="rounded-xl" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-description">Description</Label>
                  <Textarea id="edit-description" value={description} onChange={(e) => setDescription(e.target.value)} className="rounded-xl min-h-[150px]" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="edit-target">Target Amount (MYR)</Label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">MYR</span>
                      <Input id="edit-target" type="number" step="0.01" min="1" value={targetMYR} onChange={(e) => setTargetMYR(e.target.value)} className="pl-14 rounded-xl" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Deadline</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className={cn('w-full justify-start text-left font-normal rounded-xl', !deadline && 'text-muted-foreground')}>
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {deadline ? format(deadline, 'PPP') : 'Pick a date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={deadline} onSelect={setDeadline} disabled={(date) => date <= new Date()} initialFocus />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <Button onClick={handleSaveDetails} disabled={saving} className="rounded-full">
                  {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : 'Save Details'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payment Tab */}
          <TabsContent value="payment" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <QrCode className="w-5 h-5" />
                  Payment QR Code
                </CardTitle>
                <CardDescription>Upload a QR code for donors to scan</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center gap-4">
                  {qrPreview ? (
                    <div className="relative w-48 h-48 rounded-xl overflow-hidden border">
                      <img src={qrPreview} alt="Payment QR" className="w-full h-full object-contain" />
                      <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 hover:opacity-100 transition-opacity cursor-pointer">
                        <span className="text-white text-sm font-medium">Change</span>
                        <input type="file" accept="image/jpeg,image/png,image/jpg" onChange={handleQrChange} className="hidden" />
                      </label>
                    </div>
                  ) : (
                    <label className="w-48 h-48 rounded-xl border-2 border-dashed border-border hover:border-primary/50 transition-colors cursor-pointer flex flex-col items-center justify-center gap-2 bg-secondary/30">
                      <Upload className="w-8 h-8 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">Upload QR</p>
                      <input type="file" accept="image/jpeg,image/png,image/jpg" onChange={handleQrChange} className="hidden" />
                    </label>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Bank Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bank-name">Bank Name</Label>
                    <Input id="bank-name" value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="e.g., Maybank" className="rounded-xl" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="account-number">Account Number</Label>
                    <Input id="account-number" value={bankAccountNumber} onChange={(e) => setBankAccountNumber(e.target.value)} placeholder="e.g., 1234567890" className="rounded-xl" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="account-holder">Account Holder Name</Label>
                  <Input id="account-holder" value={accountHolderName} onChange={(e) => setAccountHolderName(e.target.value)} placeholder="e.g., John Doe" className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="payment-instructions">Payment Instructions</Label>
                  <Textarea id="payment-instructions" value={paymentInstructions} onChange={(e) => setPaymentInstructions(e.target.value)} placeholder="Any special instructions..." className="rounded-xl" />
                </div>
                <Button onClick={handleSavePaymentDetails} disabled={saving} className="rounded-full">
                  {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : 'Save Payment Details'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Spending Tab */}
          <TabsContent value="spending" className="space-y-6">
            {/* Legal Disclaimer */}
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Shield className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="space-y-2">
                    <h4 className="font-semibold text-amber-800">Important Legal Notice</h4>
                    <div className="text-sm text-amber-700 space-y-2">
                      <p>Before uploading receipts, please ensure you have:</p>
                      <ul className="list-disc list-inside space-y-1 ml-2">
                        <li><strong>Redacted all sensitive personal information</strong> (IC numbers, full addresses, phone numbers, etc.)</li>
                        <li><strong>Removed or blurred</strong> any third-party personal data</li>
                        <li><strong>Obtained necessary consent</strong> if any identifiable information of others is visible</li>
                      </ul>
                      <p className="pt-2">By uploading receipts, you confirm that:</p>
                      <ul className="list-disc list-inside space-y-1 ml-2">
                        <li>All information is accurate and represents legitimate campaign expenses</li>
                        <li>You have the legal right to share this documentation</li>
                        <li>You comply with Malaysia's Personal Data Protection Act 2010 (PDPA) and all applicable laws</li>
                        <li>You accept full responsibility for any legal consequences arising from uploaded content</li>
                      </ul>
                      <p className="pt-2 font-medium">Fundfair and Catching Energy Sdn Bhd are not liable for any misuse of uploaded documents or violations of privacy laws.</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Receipt className="w-5 h-5" />
                  Upload Spending Receipt
                </CardTitle>
                <CardDescription>
                  Upload receipts to show how funds are being used. Remember to redact sensitive information.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleUploadReceipt} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Amount Spent (MYR) *</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">MYR</span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0.01"
                          value={receiptAmount}
                          onChange={(e) => setReceiptAmount(e.target.value)}
                          placeholder="0.00"
                          className="pl-12 rounded-xl"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Receipt File *</Label>
                      <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed rounded-xl cursor-pointer hover:border-primary/50">
                        <Upload className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          {receiptFile ? receiptFile.name : 'Upload receipt'}
                        </span>
                        <input type="file" accept="image/jpeg,image/png,image/jpg,application/pdf" onChange={handleReceiptChange} className="hidden" />
                      </label>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Description <span className="text-red-500">*</span></Label>
                    <Input 
                      value={receiptCaption} 
                      onChange={(e) => setReceiptCaption(e.target.value)} 
                      placeholder="e.g., Medical supplies purchase" 
                      className="rounded-xl"
                      required
                    />
                    <p className="text-xs text-muted-foreground">Describe what this expense was for</p>
                  </div>
                  
                  {/* Disclaimer Checkbox */}
                  <div className="flex items-start gap-3 p-3 bg-secondary/50 rounded-xl">
                    <input
                      type="checkbox"
                      id="disclaimer-checkbox"
                      checked={disclaimerAccepted}
                      onChange={(e) => setDisclaimerAccepted(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-gray-300"
                    />
                    <label htmlFor="disclaimer-checkbox" className="text-sm text-muted-foreground cursor-pointer">
                      I confirm that I have read and understood the legal notice above. I have redacted all sensitive personal information and comply with all applicable privacy laws including PDPA 2010.
                    </label>
                  </div>
                  
                  <Button type="submit" disabled={uploadingReceipt || !disclaimerAccepted} className="rounded-full">
                    {uploadingReceipt ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</> : 'Upload Receipt'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Spending History</CardTitle>
                <CardDescription>Total spent: {formatMYR(campaign.spent_cents || 0)}</CardDescription>
              </CardHeader>
              <CardContent>
                {receipts.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No spending receipts uploaded yet</p>
                ) : (
                  <div className="space-y-3">
                    {receipts.map((receipt) => (
                      <div
                        key={receipt.id}
                        className="flex items-center justify-between p-3 bg-secondary/50 rounded-xl"
                      >
                        <button
                          onClick={() => openFileUrl(receipt.file_url, `receipt_${receipt.id}`)}
                          className="flex items-center gap-3 flex-1 text-left hover:opacity-70 transition-opacity"
                        >
                          <FileText className="w-5 h-5 text-muted-foreground" />
                          <div>
                            <p className="font-medium">{receipt.caption || 'Receipt'}</p>
                            <p className="text-xs text-muted-foreground">{formatDate(receipt.created_at)}</p>
                          </div>
                        </button>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-red-600 mr-2">-{formatMYR(receipt.amount_cents)}</span>
                          
                          {/* Edit Button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditReceipt(receipt)}
                            className="h-8 w-8 p-0 rounded-full"
                            title="Edit description"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          
                          {/* Delete Button */}
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 rounded-full text-destructive hover:text-destructive hover:bg-destructive/10"
                                title="Delete receipt"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle className="flex items-center gap-2">
                                  <AlertTriangle className="w-5 h-5 text-destructive" />
                                  Delete Receipt
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete this receipt? This will also subtract {formatMYR(receipt.amount_cents)} from your total spending amount. This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteReceipt(receipt.id)}
                                  className="rounded-full bg-destructive hover:bg-destructive/90"
                                  disabled={deletingReceiptId === receipt.id}
                                >
                                  {deletingReceiptId === receipt.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                  ) : (
                                    <Trash2 className="w-4 h-4 mr-2" />
                                  )}
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Edit Receipt Dialog */}
            <Dialog open={!!editingReceipt} onOpenChange={(open) => !open && setEditingReceipt(null)}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Edit Receipt Description</DialogTitle>
                  <DialogDescription>
                    Update the description for this spending receipt.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input
                      value={editReceiptCaption}
                      onChange={(e) => setEditReceiptCaption(e.target.value)}
                      placeholder="Enter description"
                      className="rounded-xl"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEditingReceipt(null)} className="rounded-full">
                    Cancel
                  </Button>
                  <Button onClick={handleSaveReceiptEdit} disabled={savingReceiptEdit} className="rounded-full">
                    {savingReceiptEdit ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Save Changes
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Updates Tab */}
          <TabsContent value="updates" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Megaphone className="w-5 h-5" />
                  Post an Update
                </CardTitle>
                <CardDescription>Share news and progress with your supporters</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePostUpdate} className="space-y-4">
                  <Textarea value={updateMessage} onChange={(e) => setUpdateMessage(e.target.value)} placeholder="Share an update..." className="rounded-xl min-h-[120px]" />
                  <Button type="submit" disabled={postingUpdate || !updateMessage.trim()} className="rounded-full">
                    {postingUpdate ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Posting...</> : 'Post Update'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </PageLayout>
  );
}
